import { sleep } from '@common/helpers/helpers'
import { ChannelAttachmentLinkVariant } from '@common/interfaces/channels'
import { IApi } from '../../api'

/**
 * Attempts, not retries: one immediate, then two more 500ms and 1s behind it.
 *
 * Nothing above this retries. A failed image is swapped for the file chip, which takes
 * the element its IntersectionObserver was watching with it, so the row is never asked
 * for again — one blip used to cost the image until the page was reloaded.
 */
const ATTACHMENT_LINK_ATTEMPTS = 3
const ATTACHMENT_LINK_BASE_DELAY_MS = 500

interface IAttachmentLinkRequest {
  request: Promise<string>
  progress: { isOnFinalAttempt: boolean }
}

/** Resolutions in flight, keyed by attachment id and variant — see `fetchAttachmentLink`. */
const attachmentLinkRequests = new Map<string, IAttachmentLinkRequest>()

/** Any other 4xx — expired session, no access, deleted file — answers the same way again. */
const isRetryableError = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status
  if (typeof status !== 'number') {
    return true
  }
  return status < 400 || status >= 500 || status === 408 || status === 429
}

const requestWithRetries = async (
  api: IApi,
  attachmentId: string,
  variant: ChannelAttachmentLinkVariant,
  progress: IAttachmentLinkRequest['progress'],
): Promise<string> => {
  for (let attempt = 1; ; attempt++) {
    progress.isOnFinalAttempt = attempt === ATTACHMENT_LINK_ATTEMPTS
    try {
      return (await api.channels.getAttachmentLink(attachmentId, variant)) as string
    } catch (error) {
      if (progress.isOnFinalAttempt || !isRetryableError(error)) {
        throw error
      }
    }
    await sleep(ATTACHMENT_LINK_BASE_DELAY_MS * 2 ** (attempt - 1))
  }
}

/**
 * The renderable URL for one attachment — a `data:` URL for S3-backed rows, a Box
 * embed URL for the older ones — retried, and shared while it is in flight.
 *
 * Shared, because the timeline resolves an image every time it nears the viewport and
 * hands the bytes back when it leaves — and with retries one attempt now stays in
 * flight long enough for a scroll to stack several. Dropped as soon as it settles: this
 * coalesces concurrent work, it does not cache the result, which would hold a data URL
 * per image in the channel's history.
 *
 * A caller joining on the final attempt has no retries left to share, so if that attempt
 * fails it starts a fresh request — `forget` was attached first and has cleared the entry.
 *
 * No deadline of its own — `ApiClient` already allows five minutes, and a large
 * screenshot on a slow connection has to be able to finish rather than count as failed.
 */
export const fetchAttachmentLink = (
  api: IApi,
  attachmentId: string,
  variant: ChannelAttachmentLinkVariant = ChannelAttachmentLinkVariant.Original,
): Promise<string> => {
  const key = `${attachmentId}:${variant}`
  const inFlight = attachmentLinkRequests.get(key)
  if (inFlight !== undefined) {
    return inFlight.progress.isOnFinalAttempt
      ? inFlight.request.catch(() => fetchAttachmentLink(api, attachmentId, variant))
      : inFlight.request
  }

  const progress = { isOnFinalAttempt: false }
  const request = requestWithRetries(api, attachmentId, variant, progress)

  const forget = () => attachmentLinkRequests.delete(key)
  attachmentLinkRequests.set(key, { request, progress })
  // Both branches, rather than `finally`, which passes the rejection on to a promise
  // nothing is holding.
  void request.then(forget, forget)

  return request
}
